/* eslint-disable max-len */
import React, {type RefObject} from 'react';

/**
 * The `ArrowProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ArrowProps {
    /**
     * A string representing the CSS class to be applied to the ArrowIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#000'
     */
    color1?: string;
    /**
     * A number or string representing the height of the ArrowIcon element.
     *
     * @default 33
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Arrow'
     */
    testId?: string;
    /**
     * A number or string representing the height of the ArrowIcon element.
     *
     * @default 19
     */
    width?: number | string;
}

/**
 * The `Arrow` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `ArrowProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the ArrowIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the ArrowIcon element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the ArrowIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const ArrowComponent = <Arrow className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Arrow = ({className = '', color1 = '#000', height = 33, ref, testId = 'Arrow', width = 19}: ArrowProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 19 33"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="m2.7 30.74 14.03-14.03L2.69 2.67" fill="transparent" stroke={color1} strokeLinecap="round" strokeLinejoin="round" strokeWidth="4.01" />
    </svg>
);

Arrow.displayName = 'Arrow';